/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";

import { useLogo } from "../context/LogoContext";

import ourservicesphoto from "../../src/Assets/ourprojects.png";
import Heading from "../reusable-components/fonts/Heading";
import websitephoto from "../Assets/web application.jpg";
import inpremiseappphoto from "../Assets/In Premise application.jpg";
import mobilesolutionsphoto from "../Assets/mobile solution.jpg";
import egovphoto from "../Assets/e governance.jpg";
import ecomphoto from "../Assets/e-commerc.jpg";
import softwareconsultancyphoto from "../Assets/consultancy.jpg";
import seoconsultancyphoto from "../Assets/SEO.jpg";
import chatbotsphoto from "../Assets/chatbots.jpg";
import softskillsphoto from "../Assets/softskill.jpg";

const ServiceCard = ({ id, title, description, image, reverse = false }) => {
  return (
    <motion.div
      id={id}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`mb-20 lg:mb-32 ${reverse ? "lg:pl-20" : "lg:pr-20"}`}
    >
      <div className={`flex flex-col lg:flex-row ${reverse ? "lg:flex-row-reverse" : ""} items-center gap-8 lg:gap-16`}>
        <div className="w-full lg:w-1/2 relative group overflow-hidden rounded-xl shadow-2xl">
          <motion.img
            src={image}
            alt={title}
            className="w-full h-64 lg:h-96 object-cover rounded-xl transform transition-transform duration-500"
            whileHover={{ scale: 1.02 }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
        </div>
        
        <div className="w-full lg:w-1/2">
          <motion.h3 
            className="text-2xl lg:text-3xl font-bold text-gray-800 mb-4"
            whileHover={{ color: "#3b82f6" }}
            transition={{ duration: 0.3 }}
          >
            {title}
          </motion.h3>
          <p className="text-gray-600 leading-relaxed">{description}</p>
        </div>
      </div>
    </motion.div>
  );
};

const Services = () => {
  const location = useLocation();
  const bannerRef = useRef();
  const { setFirstDivHeight } = useLogo();

  useEffect(() => {
    if (location.hash) {
      const elem = document.getElementById(location.hash.slice(1));
      if (elem) {
        elem.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, [location]);

  useEffect(() => {
    if (bannerRef.current?.offsetHeight > 0) {
      setFirstDivHeight(bannerRef.current.offsetHeight);
    }
  }, [bannerRef.current?.offsetHeight]);

  return (
    <div className="overflow-hidden font-poppins">
      {/* Hero Banner */}
      <section className="relative">
        <div className="relative h-72 lg:h-[500px] w-full overflow-hidden" ref={bannerRef}>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/80 z-10"></div>
          <img
            src={ourservicesphoto}
            alt="Our Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 z-20 flex items-center justify-center lg:justify-start px-6 lg:px-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <Heading classes="text-white text-4xl lg:text-6xl font-extrabold mb-4">
                Our Services
              </Heading>
              <motion.p 
                className="text-white text-lg lg:text-xl max-w-2xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                Innovative solutions tailored to your business needs
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 lg:py-24 px-6 lg:px-10 max-w-7xl mx-auto">
        <ServiceCard
          id="service1"
          title="Websites / Web Apps / Portals"
          description="We design and develop custom websites, web apps, and portals to meet your unique business needs. Our team of experts bring expertise and creativity to each project. Let us help you succeed online."
          image={websitephoto}
        />

        <ServiceCard
          id="service2"
          title="In-Premise Applications"
          description="We build In-Premise software applications that offer tailored & secured solutions. These are built with an ability to control your environment, security and data so that you have complete peace of mind while running your business."
          image={inpremiseappphoto}
          reverse
        />

        <ServiceCard
          id="service3"
          title="Mobile Solutions"
          description="Mobile solutions from our team help businesses reach customers on-the-go. We design and develop custom mobile apps that provide seamless, user-friendly experiences on any device. Enhance your brand and reach with a mobile solution from us."
          image={mobilesolutionsphoto}
        />

        <ServiceCard
          id="service4"
          title="e-Governance Solutions"
          description="E-governance solutions from us streamline government processes and improve citizen engagement. We design and develop custom e-governance solutions that increase efficiency, transparency, and accessibility. Partner with us to modernize your government operations and enhance citizen engagement."
          image={egovphoto}
          reverse
        />

        <ServiceCard
          id="service5"
          title="e-Commerce Solutions"
          description="We design and develop custom e-commerce websites and apps that provide seamless, user-friendly experiences for customers. Grow your business with an e-commerce solution from us."
          image={ecomphoto}
        />

        <ServiceCard
          id="service6"
          title="Software Consultancy"
          description="Software consultancy from our team helps businesses optimize and innovate. We provide expert advice and guidance on software development, design, and strategy. Partner with us for a competitive edge through innovative software solutions."
          image={softwareconsultancyphoto}
          reverse
        />

        <ServiceCard
          id="service7"
          title="Search Engine Optimization (SEO) Consultancy"
          description="With SEO solutions, we help you drive traffic and improve online visibility. We provide customized SEO strategies that help businesses rank higher in search engine results and reach more potential customers. Increase your online visibility with SEO solutions from us."
          image={seoconsultancyphoto}
        />

        <ServiceCard
          id="service8"
          title="Chatbots"
          description="Chatbots help automate customer interactions and improve efficiency. We design and develop custom chatbots that provide quick, seamless, and personalized experiences for customers. Enhance customer engagement and streamline operations with a chatbot from us."
          image={chatbotsphoto}
          reverse
        />

        <ServiceCard
          id="service9"
          title="Soft Skills Training"
          description="Soft skill training from our team helps individuals and organizations thrive. We provide customized soft skill training programs that improve communication, teamwork, leadership, and more. Enhance your professional growth and success with soft skill training from us."
          image={softskillsphoto}
        />


        
        
      </section>

      
    </div>
  );
};

export default Services;